/** Finite coastal reaches, fitted to native land/ocean crossings. Shared by
 * terrain workers, drainage, support and settlement planning. No render state. */
import { hashSeed, rngFromString } from './rng.js';
import * as pmath from './pmath.js';
import { planarField } from './landforms.js';
import type { PlanetDef } from './universe.js';
import { buildSeaStacks, seaStackHeight, type SeaStack } from './seaStacks.js';

type V3 = readonly [number, number, number];
export type CoastKind = 'pocket-beach' | 'shelving-beach' | 'rocky-headland' | 'cliff-coast';
export interface CoastalLandform {
  readonly id: string;
  readonly kind: CoastKind;
  readonly center: V3;
  readonly along: V3;
  /** Positive cross-shore coordinates point inland. */
  readonly inland: V3;
  readonly halfLengthM: number;
  readonly inlandM: number;
  readonly offshoreM: number;
  readonly cliffM: number;
  readonly beachM: number;
  readonly projectionM: number;
  readonly shore: readonly number[];
  readonly seed: number;
  readonly stacks: readonly SeaStack[];
}
export interface CoastalSample { weight: number; heightM: number; beach: number; rock: number }
export const coastalSample = (): CoastalSample => ({weight:0,heightM:0,beach:0,rock:0});
const smooth = (v:number):number => {const t=Math.max(0,Math.min(1,v));return t*t*(3-2*t);};
const norm = (x:number,y:number,z:number):V3 => {const l=Math.sqrt(x*x+y*y+z*z);return [x/l,y/l,z/l];};
const dot = (a:V3,b:V3):number => a[0]*b[0]+a[1]*b[1]+a[2]*b[2];
const cross = (a:V3,b:V3):V3 => [a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];

export function coastalDirection(f:CoastalLandform,radius:number,u:number,v:number):V3 {
  return norm(f.center[0]*radius+f.along[0]*u+f.inland[0]*v,
    f.center[1]*radius+f.along[1]*u+f.inland[1]*v,
    f.center[2]*radius+f.along[2]*u+f.inland[2]*v);
}

/** Smooth interpolation through surveyed native waterline knots. */
export function coastalShore(f:CoastalLandform,u:number):number {
  const s=Math.max(-1,Math.min(1,u/f.halfLengthM)),t=(s+1)*3,k=Math.min(5,Math.floor(t)),q=t-k;
  const a=f.shore[Math.max(0,k-1)],b=f.shore[k],c=f.shore[k+1],d=f.shore[Math.min(6,k+2)];
  return b+.5*q*(c-a+q*(2*a-5*b+4*c-d+q*(3*(b-c)+d-a)));
}

export class CoastalLandforms {
  readonly features: readonly CoastalLandform[];
  private readonly cells=new Map<number,readonly CoastalLandform[]>();
  private readonly cellM:number;
  private readonly gridSize:number;
  private readonly seaM:number;

  constructor(private readonly def:PlanetDef,native:(x:number,y:number,z:number)=>number,
    continental:(x:number,y:number,z:number)=>number) {
    const R=def.radius;
    this.seaM=(def.seaRadius??R)-R;
    this.cellM=Math.min(1400,R*.1);this.gridSize=Math.ceil(2*R/this.cellM)+3;
    const features:CoastalLandform[]=[];this.features=features;
    if(def.seaRadius===null||def.terrain.amplitudeRel<.001||def.archetype==='ice'||def.archetype==='volcanic'){
      Object.freeze(features);return;
    }
    // Keep the original pocket reach survey and identities. A second, denser
    // survey fits shorter reaches into the intervening native coast.
    for(const pass of [0,1]){
    const count=pass?4096:1536,limit=pass?40:16;
    const phase=rngFromString(`${def.seed}:coastal-reaches:v${pass?2:1}`)()*pmath.TWO_PI;
    const halfLengthM=Math.min(pass?520:880,R*(pass?.04:.065)),inlandM=Math.min(650,R*.052),offshoreM=Math.min(500,R*.042);
    for(let candidate=0;candidate<count&&features.length<limit;candidate++) {
      // Permutation distributes a bounded feature budget over both hemispheres.
      const i=(candidate*641)%count;
      const y=1-2*(i+.5)/count,a=phase+i*2.399963229728653,r=Math.sqrt(1-y*y);
      let center:V3=[r*pmath.cos(a),y,r*pmath.sin(a)];
      // Cheap continental rejection before surveying the complete native ground.
      if(Math.abs(continental(...center)-this.seaM)>100)continue;
      if(features.some(f=>dot(center,f.center)>1-(((halfLengthM+f.halfLengthM)*1.4/R)**2)/2))continue;
      if(Math.abs(native(...center)-this.seaM)>55)continue;
      const e=norm(center[2],0,-center[0]),n=cross(center,e);
      const probe=(x:number,y:number)=>native(...norm(center[0]*R+e[0]*x+n[0]*y,
        center[1]*R+e[1]*x+n[1]*y,center[2]*R+e[2]*x+n[2]*y));
      const gx=probe(100,0)-probe(-100,0),gy=probe(0,100)-probe(0,-100);
      if(gx*gx+gy*gy<4)continue;
      let inland=norm(e[0]*gx+n[0]*gy,e[1]*gx+n[1]*gy,e[2]*gx+n[2]*gy),along=cross(inland,center);
      const draft:CoastalLandform={id:`${def.id}:${pass?'reach':'coast'}:${i}`,kind:'pocket-beach',center,along,inland,
        halfLengthM,inlandM,offshoreM,cliffM:0,beachM:0,projectionM:0,shore:[],stacks:[],seed:hashSeed(`${def.seed}:coast-rock:${i}`)};
      const sample=(u:number,v:number)=>native(...coastalDirection(draft,R,u,v))-this.seaM;
      // Genuine ocean-facing ground, not a ring around an inland lake.
      if(sample(0,-offshoreM)>-6||sample(0,inlandM)<10)continue;
      if(continental(...coastalDirection(draft,R,0,-offshoreM*1.5))>this.seaM-8)continue;
      let lo=-offshoreM*.8,hi=inlandM*.8;
      if(sample(0,lo)>=0||sample(0,hi)<=0)continue;
      for(let k=0;k<18;k++){const mid=(lo+hi)*.5;if(sample(0,mid)>0)hi=mid;else lo=mid;}
      center=coastalDirection(draft,R,0,(lo+hi)*.5);
      const d=dot(inland,center);inland=norm(inland[0]-center[0]*d,inland[1]-center[1]*d,inland[2]-center[2]*d);
      along=cross(inland,center);
      const fit={...draft,center,inland,along},shore:number[]=[];
      for(let k=0;k<7;k++) {
        const u=(k/3-1)*halfLengthM;
        let lo=-offshoreM*.62,hi=inlandM*.62;
        const h=(v:number)=>native(...coastalDirection(fit,R,u,v))-this.seaM;
        if(h(lo)>-2||h(hi)<2)break;
        for(let j=0;j<16;j++){const mid=(lo+hi)*.5;if(h(mid)>0)hi=mid;else lo=mid;}
        shore.push((lo+hi)*.5);
      }
      if(shore.length!==7||shore.some((v,k)=>k>0&&Math.abs(v-shore[k-1])>halfLengthM*.36))continue;
      if(features.some(f=>dot(center,f.center)>1-(((halfLengthM+f.halfLengthM)*1.4/R)**2)/2))continue;
      const random=rngFromString(`${def.seed}:coast-shape:${i}`);
      const back=native(...coastalDirection(fit,R,0,inlandM*.7))-this.seaM;
      const geology=planarField(center[0]*R/2400,center[1]*R/2400,center[2]*R/2400,hashSeed(`${def.seed}:coast-geology`));
      const kind:CoastKind=pass===0?'pocket-beach':back>65||(back>30&&geology>.22)?'cliff-coast':back<28&&geology<.2?'shelving-beach':'rocky-headland';
      features.push(Object.freeze({...fit,kind,shore:Object.freeze(shore),center:Object.freeze(center),along:Object.freeze(along),inland:Object.freeze(inland),
        cliffM:Math.min(135,inlandM*(.14+random()*.075)),beachM:Math.min(inlandM*.115,42+random()*32),
        projectionM:halfLengthM*(.13+random()*.08)}));
    }
    }
    Object.freeze(features);
    const mutable=new Map<number,CoastalLandform[]>();
    for(const f of features){
      const reach=Math.sqrt(f.halfLengthM*f.halfLengthM+f.inlandM*f.inlandM),bound=reach+R*(1-R/Math.sqrt(R*R+reach*reach));
      const lo=f.center.map(v=>Math.max(0,Math.floor((v*R-bound+R)/this.cellM)));
      const hi=f.center.map(v=>Math.min(this.gridSize-1,Math.floor((v*R+bound+R)/this.cellM)));
      for(let x=lo[0];x<=hi[0];x++)for(let y=lo[1];y<=hi[1];y++)for(let z=lo[2];z<=hi[2];z++){
        const key=this.key(x,y,z);let list=mutable.get(key);if(!list)mutable.set(key,list=[]);list.push(f);
      }
    }
    for(const [key,list] of mutable)this.cells.set(key,Object.freeze(list));
    // Fit remnants only after the original reach survey and index exist. The
    // callback sees the same native/coastal blend as drainage and support.
    const sample=coastalSample();
    for(const f of features){
      const stacks=buildSeaStacks(f,def,(u,v)=>{
        const d=coastalDirection(f,R,u,v),base=native(...d);
        this.sample(...d,sample);return base+(sample.heightM-base)*sample.weight;
      });
      (f.stacks as SeaStack[]).push(...stacks);Object.freeze(f.stacks);
    }
  }
  private key(x:number,y:number,z:number):number{return (x*this.gridSize+y)*this.gridSize+z;}

  sample(dx:number,dy:number,dz:number,out:CoastalSample):CoastalSample {
    out.weight=out.heightM=out.beach=out.rock=0;
    const R=this.def.radius,c=this.cellM;
    const candidates=this.cells.get(this.key(Math.floor((dx*R+R)/c),Math.floor((dy*R+R)/c),Math.floor((dz*R+R)/c)));
    if(!candidates)return out;
    let sum=0,uncovered=1;
    for(const f of candidates){
      const facing=dx*f.center[0]+dy*f.center[1]+dz*f.center[2];if(facing<=0)continue;
      const u=(dx*f.along[0]+dy*f.along[1]+dz*f.along[2])*R/facing,s=u/f.halfLengthM;
      if(Math.abs(s)>=1)continue;
      const v=(dx*f.inland[0]+dy*f.inland[1]+dz*f.inland[2])*R/facing;
      const w=smooth((1-Math.abs(s))/.18)*smooth((v+f.offshoreM)/(f.offshoreM*.36))*smooth((f.inlandM-v)/(f.inlandM*.36));
      if(w<=0)continue;
      if(f.kind!=='pocket-beach'){
        const scale=Math.min(1,f.inlandM/450),angular=planarField(u/(105*scale),0,5.2,f.seed);
        const protrusion=f.kind==='rocky-headland'?f.projectionM*(.3+.7*(1-Math.abs(s))):0;
        const cross=v-coastalShore(f,u)+protrusion;
        const shelving=f.kind==='shelving-beach';
        const foot=shelving?f.beachM*1.5:Math.max(0,(f.kind==='rocky-headland'?8:1)+angular*12*scale);
        const run=shelving?220*scale:(f.kind==='rocky-headland'?48:32)*scale;
        const q=smooth((cross-foot)/run);
        const cliff=shelving?7*scale:f.cliffM*(f.kind==='rocky-headland'?.46:.84)*(1+angular*.34);
        const slope=shelving?.047:f.kind==='rocky-headland'?.16:.27;
        const detail=planarField(u/(45*scale),cross/(65*scale),1.3,f.seed)*(shelving?.10:2.4*scale*q);
        const base=this.seaM+cross*slope+cliff*q+detail;
        let target=base;
        for(const stack of f.stacks)target=seaStackHeight(stack,u,v,this.seaM,target);
        sum+=w;uncovered*=1-w;out.heightM+=target*w;
        out.beach+=(target>base?0:shelving?1:1-smooth(cross/(12*scale)))*w;
        out.rock+=(target>base?1:shelving?0:1)*w;
        continue;
      }
      // Unequal rocky headlands enclose one continuous concave sand pocket.
      const left=smooth((.34-Math.abs(s+.57))/.19),right=smooth((.29-Math.abs(s-.59))/.17);
      const rock=Math.max(left,right),bay=1-smooth(Math.abs(s)/.52);
      const shore=coastalShore(f,u)+bay*f.projectionM*.38-f.projectionM*(left+right*.78);
      const cross=v-shore,scale=Math.min(1,f.inlandM/450);
      // Fractures displace full rock faces, rather than adding fine noise to a
      // smooth curtain. Their angular carrier is independent of mesh triangles.
      const fracture=planarField(u/(68*scale),0,7.1,f.seed);
      const broad=planarField(u/(175*scale),0,4.2,f.seed);
      const beach=f.beachM*(1-rock)+(fracture*25+broad*26)*scale*smooth(cross/(24*scale));
      // One recessed gully supplies gradual access to the bluff. It does not
      // cut a new river or claim an inland water plane.
      const access=(1-smooth((Math.abs(s+.24)-.025)/.09))*(1-rock);
      const faceRun=Math.min(30+(1-rock)*24,f.inlandM*.10)+access*Math.min(350,f.inlandM*.54);
      const q=Math.max(0,Math.min(1,(cross-beach)/faceRun));
      // A planar face with eased feet and brow; no overhang/collision proxy.
      const ramp=q<.12?q*q/.2112:q>.88?1-(1-q)*(1-q)/.2112:(q-.06)/.88;
      const crest=f.cliffM*(.86+left*.24-right*.08+.30*broad+.16*fracture);
      const facet=planarField(u/(42*scale),cross/(64*scale),3.7,f.seed);
      const detail=facet*(.12+5.5*scale*smooth((cross-beach)/(18*scale)))*(1-smooth((cross-beach-faceRun)/(160*scale)));
      const shelf=cross<0?cross*(.085+.32*rock):cross*.085;
      const base=this.seaM+shelf+crest*ramp+detail;
      let target=base;
      for(const stack of f.stacks)target=seaStackHeight(stack,u,v,this.seaM,target);
      sum+=w;uncovered*=1-w;out.heightM+=target*w;
      out.beach+=(target>base?0:(1-rock)*(1-smooth((cross-beach)/18)))*w;
      out.rock+=(target>base?1:Math.max(rock,smooth((cross-beach)/12)*(1-smooth((cross-beach-faceRun)/35))))*w;
    }
    if(sum>0){out.weight=1-uncovered;out.heightM/=sum;out.beach/=sum;out.rock/=sum;}
    return out;
  }
}
